import type { GooglePlacesSuggestionResult } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { type SelectOption } from 'noddi-ui-web';

type AddressSuggestionOption = SelectOption<string> & {
  originalData?: GooglePlacesSuggestionResult;
};

const mapAddressSuggestionToOption = (suggestion: GooglePlacesSuggestionResult): AddressSuggestionOption => ({
  value: suggestion.placeId,
  label: `${suggestion.mainText} - ${suggestion.secondaryText}`,
  originalData: suggestion
});

const toAddressSuggestionOptions = (suggestions: unknown): AddressSuggestionOption[] | undefined => {
  if (!Array.isArray(suggestions)) {
    return undefined;
  }
  return suggestions.map(mapAddressSuggestionToOption);
};

export const renderAddressSuggestionOption = (option: AddressSuggestionOption) => {
  if (!option.originalData) {
    return option.label;
  }

  const suggestion = option.originalData;

  return (
    <div className='flex flex-col'>
      <span className='font-medium'>{suggestion.mainText}</span>
      <span className='text-sm text-muted-foreground'>{suggestion.secondaryText}</span>
    </div>
  );
};

export const useAddressSuggestions = (searchTerm: string, countryCodes?: string[]) => {
  const {
    data: addressSuggestions,
    isLoading: isAddressSuggestionsLoading,
    error: addressSuggestionsError
  } = noddiAsync.useGet({
    type: URLKeys.getAddressSuggestions,
    input: { queryInput: searchTerm, countryCodes },
    queryConfig: {
      // Places autocomplete is billed per request. The backend also no-ops under 3 characters.
      enabled: searchTerm.trim().length >= 3
    }
  });

  const suggestions = Array.isArray(addressSuggestions) ? addressSuggestions : undefined;

  return {
    addressSuggestions: suggestions,
    addressSuggestionOptions: toAddressSuggestionOptions(suggestions),
    isAddressSuggestionsLoading,
    addressSuggestionsError
  };
};
